import React, { useContext } from 'react';
import { styled } from '@mui/material';

import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import { Button, Box, Icon, Text, TextInput, Stack, IconButton } from 'shell/components';
import PeerEvaluationTableInstructor from 'components/organisms/PeerEvaluation/PeerEvaluationTableInstructor';
import PeerEvaluationTableStudent from 'components/organisms/PeerEvaluation/PeerEvaluationTableStudent';
import { useViewport } from 'contexts/viewport';

const PeerEvaluationTable = ({
    peerEvaluationList = [],
    setIsFilterDrawerOpen,
    searchBarInput,
    onChangeSearchBarInput,
}) => {
    const { device } = useViewport();
    const { auth, handleInstructorPages, INSTRUCTOR_PAGES } = useContext(PageContext);

    const renderHeaderMobile = () => {
        return (
            <Stack direction={'column'} spacing={6} sx={{ padding: '16px 16px' }}>
                <Text variant={'h6'} weight={'semibold'}>
                    Peer Evaluations
                </Text>
                <CustomHeaderBox direction={'row'} spacing={2} alignItems={'center'}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setIsFilterDrawerOpen(true)}
                    >
                        Filters
                    </Button>
                    <CustomMobileTextInput
                        disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                        endIcon={
                            searchBarInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => onChangeSearchBarInput('')}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={searchBarInput}
                        onChange={(e) => onChangeSearchBarInput(e.target.value)}
                    />
                </CustomHeaderBox>
            </Stack>
        );
    };

    const renderHeaderTablet = () => {
        return (
            <Stack direction={'column'} spacing={6}>
                <Text variant={'h6'} weight={'semibold'}>
                    Peer Evaluations
                </Text>
                <Stack alignItems={'center'} direction={'row'} spacing={2} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Button
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'tertiary'}
                            onClick={() => setIsFilterDrawerOpen(true)}
                        >
                            Filters
                        </Button>
                        <CustomTextInput
                            disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                            endIcon={
                                searchBarInput ? (
                                    <IconButton
                                        aria-label={'clear search input'}
                                        kind={'exit'}
                                        variant={'square'}
                                        onClick={() => onChangeSearchBarInput('')}
                                        onMouseDown={(e) => e.preventDefault()}
                                    />
                                ) : (
                                    <Icon kind={'search'} />
                                )
                            }
                            placeholder={'Search'}
                            value={searchBarInput}
                            onChange={(e) => onChangeSearchBarInput(e.target.value)}
                        />
                    </Stack>
                    {auth.role === 'instructor' && (
                        <Button
                            data-cy={'create-evaluation-button'}
                            onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.CREATE)}
                        >
                            Create Evaluation
                        </Button>
                    )}
                </Stack>
            </Stack>
        );
    };

    const renderHeaderDesktop = () => {
        return (
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Text variant={'h6'} weight={'semibold'}>
                    Peer Evaluations
                </Text>
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setIsFilterDrawerOpen(true)}
                    >
                        Filters
                    </Button>
                    <CustomTextInput
                        disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                        endIcon={
                            searchBarInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => onChangeSearchBarInput('')}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={searchBarInput}
                        onChange={(e) => onChangeSearchBarInput(e.target.value)}
                    />
                    {auth.role === 'instructor' && (
                        <Button
                            data-cy={'create-evaluation-button'}
                            onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.CREATE)}
                        >
                            Create Evaluation
                        </Button>
                    )}
                </Stack>
            </Stack>
        );
    };

    const responsiveHeader = {
        mobile: renderHeaderMobile(),
        tablet: renderHeaderTablet(),
        desktop: renderHeaderDesktop(),
    };

    return (
        <Stack spacing={5}>
            {responsiveHeader[device]}
            <CustomStack>
                <Stack>
                    {auth.role === 'instructor' ? (
                        <PeerEvaluationTableInstructor peerEvaluationList={peerEvaluationList} />
                    ) : (
                        <PeerEvaluationTableStudent peerEvaluationList={peerEvaluationList} />
                    )}
                </Stack>
            </CustomStack>
        </Stack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(6, 5),
        ...(device === 'mobile' && {
            paddingBottom: theme.spacing(8),
        }),
        borderRadius: '12px 12px 0 0',
        width: '100%',
        minHeight: 'calc(100vh - 64px - 20px - 58px)',
        background: theme.palette.ensightful.shades[0],
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const CustomMobileTextInput = styled(TextInput)(() => {
    return {
        width: '100%',
    };
});

const CustomHeaderBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '30% 70%',
        gridGap: theme.spacing(4),
        padding: theme.spacing(0, 4),
    };
});
export default PeerEvaluationTable;
